'use client'

import React from 'react'
import type { PropsWithChildren } from 'react'
import { createPortal } from 'react-dom'

type ModalProps = PropsWithChildren<{}> & {
  isOpen: boolean
  size?: 'sm' | 'md' | 'lg'
  onClose?: () => void
  outsideClickClose?: boolean
  className?: string
}

const ModalContext = React.createContext<{
  size?: 'sm' | 'md' | 'lg'
  isOpen: boolean
  onClose?: () => void
}>({
  size: 'sm',
  isOpen: false,
  onClose: () => {}
})

const Modal = ({
  children,
  isOpen,
  size = 'sm',
  onClose,
  outsideClickClose = true,
  className
}: ModalProps) => {
  const contextValue = React.useMemo(
    () => ({ size, isOpen, onClose }),
    [size, isOpen, onClose]
  )

  const sizeWidthStyle = {
    sm: '352px',
    md: '544px',
    lg: '763px'
  }

  const customStyles = {
    overlay: {
      backgroundColor: 'rgba(0, 0, 0, 0.4)',
      zIndex: 1000
    },
    content: {
      boxShadow: '0px 6px 16px 0px #3D47510F, 0px 0px 4px 1px #3D47510A',
      border: 'none',
      borderRadius: '8px',
      padding: '16px',
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
      width: '100%',
      maxWidth: sizeWidthStyle[size],
      maxHeight: '85vh',
      overflowY: 'auto' as const,
      backgroundColor: 'FFF'
    }
  }

  const containerRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        outsideClickClose
      ) {
        onClose?.()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose, outsideClickClose])

  return (
    <ModalContext.Provider value={contextValue}>
      {isOpen &&
        createPortal(
          <div className="bg-black/40 inset-0 fixed z-[1000]">
            <div
              style={{
                ...customStyles.content
              }}
              ref={containerRef}
              className={`bg-white absolute rounded-lg right-auto bottom-auto mt-2 shadow-md border-0 p-2 ${className}`}
            >
              {children}
            </div>
          </div>,
          document.body
        )}
    </ModalContext.Provider>
  )
}

type ModalHeaderProps = PropsWithChildren<{}> &
  React.HTMLAttributes<HTMLDivElement>

const ModalHeader = ({ children, className, ...props }: ModalHeaderProps) => {
  const context = React.useContext(ModalContext)

  if (!context) {
    throw new Error('ModalHeader must be used within a Modal component')
  }

  return (
    <div className={`flex justify-between ${className}`} {...props}>
      {children}
    </div>
  )
}

type ModalTitleProps = PropsWithChildren<{}> &
  React.HTMLAttributes<HTMLParagraphElement>

const ModalTitle = ({ children, className, ...props }: ModalTitleProps) => {
  const context = React.useContext(ModalContext)

  if (!context) {
    throw new Error('ModalTitle must be used within a Modal component')
  }

  return (
    <p
      className={`heading-6-regular text-neutral-gray-700 ${className}`}
      {...props}
    >
      {children}
    </p>
  )
}

const ModalClose = ({ className }: { className?: string }) => {
  const context = React.useContext(ModalContext)

  if (!context) {
    throw new Error('ModalClose must be used within a Modal component')
  }

  const { onClose } = context

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="currentColor"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      className={`size-6 cursor-pointer shrink-0 ${className}`}
      onClick={onClose}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 18 18 6M6 6l12 12"
      />
    </svg>
  )
}

type ModalContentProps = PropsWithChildren<{}> &
  React.HTMLAttributes<HTMLDivElement>

const ModalContent = ({ children, className, ...props }: ModalContentProps) => {
  const context = React.useContext(ModalContext)

  if (!context) {
    throw new Error('ModalContent must be used within a Modal component')
  }

  return (
    <div className={`mt-4 ${className}`} {...props}>
      {children}
    </div>
  )
}

type ModalActionsProps = PropsWithChildren<{}> &
  React.HTMLAttributes<HTMLDivElement>

const ModalActions = ({ children, className, ...props }: ModalActionsProps) => (
  <div className={`mt-4 flex justify-end gap-4 ${className}`} {...props}>
    {children}
  </div>
)

Modal.Header = ModalHeader
Modal.Title = ModalTitle
Modal.Close = ModalClose
Modal.Content = ModalContent
Modal.Actions = ModalActions

export default Modal
